import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Mail, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { AuthShell } from "@/pages/Login";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.reset_link) return;
    await navigator.clipboard.writeText(sent.reset_link);
    toast.success("Link copied");
  };

  return (
    <AuthShell
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a reset link."
      footer={
        <>
          Remembered it?{" "}
          <Link
            to="/login"
            data-testid="back-to-login"
            className="font-semibold text-indigo-700 hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      {sent ? (
        <div data-testid="forgot-sent-state" className="space-y-4">
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
            <div className="text-sm text-emerald-800">
              <p className="font-semibold">Check your inbox</p>
              <p className="mt-1 text-emerald-700">
                If an account exists for <span className="font-medium">{email}</span>, we've sent a reset link. It expires in 1 hour.
              </p>
            </div>
          </div>
          {sent.demo_mode && sent.reset_link && (
            <div
              data-testid="demo-reset-link"
              className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">
                Demo mode · email not sent
              </p>
              <p className="text-xs text-amber-800">
                No <code>RESEND_API_KEY</code> configured yet. Use this link
                directly:
              </p>
              <div className="flex items-center gap-2 rounded-xl bg-white p-2">
                <code className="flex-1 truncate font-mono text-[11px] text-slate-700">
                  {sent.reset_link}
                </code>
                <button
                  onClick={copyLink}
                  className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-700"
                  aria-label="Copy"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <Link
                to={new URL(sent.reset_link).pathname + new URL(sent.reset_link).search}
                data-testid="demo-open-reset-link"
                className="inline-block text-xs font-semibold text-amber-800 underline"
              >
                Open reset page →
              </Link>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="forgot-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
            />
          </div>
          <Button
            type="submit"
            data-testid="forgot-submit-button"
            disabled={loading}
            className="group h-11 w-full gap-2 rounded-full bg-[#1A1D20] text-sm font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send reset link
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
